import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, LoadingBlock, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatDate, REQUEST_STATUS_LABELS, euro } from "@/lib/format";
import { MapPin, PlusCircle, X, UserCheck, Phone, BadgeEuro } from "lucide-react";

const CANCELABLE = ["inviata", "in_valutazione"];

export default function MieRichieste() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["requests"], queryFn: async () => (await api.get("/requests")).data });
  const requests = data?.requests || [];

  const cancel = async (id) => {
    if (!window.confirm("Vuoi annullare questa richiesta?")) return;
    try {
      await api.patch(`/requests/${id}/cancel`);
      toast.success("Richiesta annullata.");
      qc.invalidateQueries({ queryKey: ["requests"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Le mie richieste">
      {isLoading ? <LoadingBlock /> : requests.length === 0 ? (
        <EmptyState title="Nessuna richiesta" subtitle="Crea la tua prima richiesta di prestazione a domicilio."
          action={<Link to="/paziente/nuova-richiesta"><Button className="bg-[#087A73] text-white rounded-xl h-12 px-6"><PlusCircle className="mr-2 h-5 w-5" /> Nuova richiesta</Button></Link>} />
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <Card key={r.id} data-testid={`request-row-${r.id}`} className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-bold text-[#102A2A]">{r.service_name}</p>
                  <p className="text-sm text-[#5C6B68] flex items-center gap-1 mt-1"><MapPin className="h-4 w-4" /> {r.municipality} · {formatDate(r.preferred_date)} · {r.preferred_time_range}</p>
                  {r.urgency === "prioritaria" && <span className="text-xs text-[#8a6400] font-semibold">Prioritaria</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={r.status} label={REQUEST_STATUS_LABELS[r.status]} testid={`request-status-${r.id}`} />
                  {CANCELABLE.includes(r.status) && (
                    <Button data-testid={`cancel-request-${r.id}`} onClick={() => cancel(r.id)} variant="outline" className="h-9 px-3 rounded-xl border-2 border-[#C0392B]/40 text-[#C0392B] hover:bg-[#F9E3E0]"><X className="h-4 w-4 mr-1" /> Annulla</Button>
                  )}
                </div>
              </div>
              {r.status === "in_gestione" && r.assigned_professional_name && (
                <div data-testid={`assigned-info-${r.id}`} className="bg-[#EAF3EF] border border-[#D2E3DB] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-2 text-sm text-[#102A2A]">
                    <UserCheck className="h-5 w-5 text-[#087A73] shrink-0 mt-0.5" />
                    <span>Presa in carico da <strong>{r.assigned_professional_name}</strong>{r.assigned_professional_profession ? ` · ${r.assigned_professional_profession}` : ""}. Ti contatterà al recapito che hai indicato per organizzare la visita.</span>
                  </div>
                  {r.assigned_professional_phone && (
                    <a href={`tel:${r.assigned_professional_phone}`} data-testid={`call-pro-${r.id}`} className="inline-flex items-center justify-center rounded-xl h-10 px-4 border-2 border-[#087A73] text-[#087A73] text-sm font-semibold hover:bg-[#087A73]/10 transition-colors shrink-0"><Phone className="h-4 w-4 mr-1" /> {r.assigned_professional_phone}</a>
                  )}
                </div>
              )}
              {r.agreed_price_cents != null && (
                <div data-testid={`tariffa-${r.id}`} className="bg-[#E0EFEA] border border-[#087A73]/30 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <BadgeEuro className="h-5 w-5 text-[#087A73] shrink-0" />
                  <span className="text-[#102A2A]">Tariffa concordata: <strong className="text-[#087A73]">{euro(r.agreed_price_cents)}</strong> <span className="text-xs text-[#5C6B68]">· il pagamento avviene direttamente con il medico, fuori dalla piattaforma</span></span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
